/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import dayjs from 'dayjs';
import Component from 'can/component/component';
import FeedService from 'scripts/services/base-feed-service';
import dtm from 'scripts/services/dtm';
import template from './hole-detail.stache';
import deviceUtil from 'scripts/utils/device';
import urlUtil from 'scripts/utils/url';
import settings from 'scripts/utils/settings';
import _ from 'lodash';

const moduleSettings = settings.getModuleSettings('holeDetailStats');
const isHoleDetailEnabled = moduleSettings.isEnabled;
const disabledMessage = moduleSettings.unavailabilityMessage;

const holePageUrl = window.globalSettings.pagePaths.holes;
const coursePageUrl = window.globalSettings.pagePaths.course;
const holeFeedUrl = window.globalSettings.feeds.smt.hole;
const championshipUrl = window.globalSettings.feeds.smt.championship;
const iframeEnvironment =
    window.globalSettings.iframeEnvironment ||
    'usgamensbp.smtinternetservices.com';
const postMessagesSource = `https://${iframeEnvironment}`;

const TABS_SELECTOR = '.s-tabs__item';
const keys = {
    left: 37,
    up: 38,
    right: 39,
    down: 40,
};
const direction = {
    37: -1,
    38: -1,
    39: 1,
    40: 1,
};

const holeData = can.Map.extend({
    define: {
        id: {
            type: 'number',
        },
        summary: {
            type: 'string',
        },
        flyoverVideo: {
            type: 'string',
        },
        photo: {
            type: '*',
        },
        overviewImage: {
            type: '*',
        },
        playThisHoleLink: {
            type: 'string',
        },
        defaultTab: {
            type: 'string',
        },
        excludedTabs: {
            type: 'array',
        },
    },
});

const defaultErrorMessage = 'Problems with data retrieving...';
const TABS = [
    { title: 'Hole Insights', value: 'INSIGHTS' },
    { title: 'Flyover Video', value: 'FLYOVER_VIDEO' },
    { title: 'Photo', value: 'PHOTO' },
];

Component.extend({
    tag: 'hole-detail',
    template: template,
    viewModel: {
        coursePageUrl: coursePageUrl,
        define: {
            isEnabled: {
                type: 'boolean',
                value: isHoleDetailEnabled,
            },
            disabledMessage: {
                type: 'string',
                value: disabledMessage,
            },
            holes: {
                type: 'array',
                value: [
                    1,
                    2,
                    3,
                    4,
                    5,
                    6,
                    7,
                    8,
                    9,
                    10,
                    11,
                    12,
                    13,
                    14,
                    15,
                    16,
                    17,
                    18,
                ],
            },
            data: {
                Type: holeData,
            },
            isAnyTabSelected: {
                value: false,
            },
            showError: {
                type: 'boolean',
                value: false,
            },
            loading: {
                type: 'boolean',
                value: true,
            },
            errorMessage: {
                type: 'string',
                value: '',
            },
            isSafari: {
                type: 'boolean',
                get() {
                    return deviceUtil.isSafari;
                },
            },
            tabs: {
                get() {
                    const excludedTabs = _.get(
                        this.attr('data').serialize(),
                        'tabsConfiguration.excludedTabs',
                        [],
                    );

                    return TABS.filter(
                        ({ value }) => !excludedTabs.includes(value),
                    );
                },
            },

            defaultTab: {
                get() {
                    const defaultTab =
                        this.attr('data.tabsConfiguration.defaultTab') ||
                        _.get(this.attr('tabs'), '[0].value');

                    return defaultTab;
                },
            },
        },

        displayErrorMessage() {
            return this.errorMessage;
        },

        onSelectChange(viewModel, target) {
            const link = this.holeLink(target.val());
            window.location.href = link;
        },

        prevHole() {
            const id = parseInt(this.attr('data.id')) - 1;

            return id || 1;
        },
        nextHole() {
            const id = parseInt(this.attr('data.id')) + 1;

            return id;
        },
        holeLink(holeId) {
            return can.sub(holePageUrl, { holeId });
        },

        isActive(identifier) {
            return parseInt(identifier) === this.attr('data.id');
        },

        setTab(tabs, tabsContent, tab, name) {
            tabs.removeClass('is-active')
                .attr('aria-selected', 'false')
                .attr('tabindex', '-1');
            tab.addClass('is-active')
                .attr('aria-selected', 'true')
                .removeAttr('tabindex');

            const navContent = $('.c-hole-detail__main-content');

            if (name === 'INSIGHTS') {
                navContent.addClass('c-hole-detail__main-content--insights');
            } else {
                navContent.removeClass('c-hole-detail__main-content--insights');
            }

            tabsContent.each(function() {
                $(this).hide();
                if ($(this).data('name') === name) {
                    $(this).show();
                }
            });
        },

        setTabContentState(name) {
            const videoModel = this.video.viewModel();

            if (name === 'FLYOVER_VIDEO') {
                if ('play' in videoModel) {
                    return videoModel.play();
                }
            }

            if ('pause' in videoModel) {
                videoModel.pause();
            }

            if (name === 'PHOTO') {
                this.photo.viewModel().show();
            }
        },
    },

    events: {
        inserted(element) {
            this.video = element.find('brightcove-video');
            this.tabs = element.find('.s-tabs__item');
            this.photo = element.find('.s-tab-content__item image-cloudinary');
            this.tabsContent = element.find('.s-tab-content__item');

            this.setDefaultTab();
            urlUtil.onLocationHashChanged(this.setDefaultTab.bind(this));

            this.viewModel.attr(
                'errorMessage',
                this.viewModel.attr('data.errorMessage') || defaultErrorMessage,
            );

            const holeId = this.viewModel.attr('data.id');

            window.addEventListener('message', this.handlePostMessage, false);

            if (holeId) {
                element.find('.s-select').val(holeId);
                new FeedService({
                    feedUrl: holeFeedUrl,
                    fromCache: false,
                    autoUpdate: false,
                })
                    .on('data', (type, data) => {
                        const { par = '', yards = '' } = _.get(
                            data,
                            'hole',
                            {},
                        );

                        if (par && yards) {
                            this.viewModel.attr({
                                par,
                                yards,
                            });
                        } else {
                            console.error('Wrong hole information');
                        }
                    })
                    .on('error', (error) => {
                        console.error(
                            'Hole information has not been fetched',
                            error,
                        );
                    })
                    .fetch({ identifier: holeId });
            } else {
                console.error('Hole id is not specified');
            }

            new FeedService({
                feedUrl: championshipUrl,
                fromCache: false,
                autoUpdate: false,
            })
                .on('data', (type, data) => {
                    const now = dayjs().valueOf();
                    const startDate = dayjs(data.startDate).valueOf();
                    const availableTabs = this.viewModel.attr('tabs');

                    if (
                        !this.viewModel.isAnyTabSelected &&
                        availableTabs.includes('FLYOVER_VIDEO')
                    ) {
                        if (now < startDate) {
                            const videoTab = element.find(
                                '.s-tabs__item[data-name="FLYOVER_VIDEO"]',
                            );
                            this.viewModel.setTab(
                                this.tabs,
                                this.tabsContent,
                                videoTab,
                                'FLYOVER_VIDEO',
                            );
                            let videoModel = this.video.viewModel();
                            videoModel.attr('autoplay', true);
                            videoModel.playWithSoundCheck();
                        }
                    }
                })
                .on('error', (error) => {
                    console.error(
                        'Championship information has not been fetched',
                        error,
                    );
                })
                .fetch();

            this.initTabs(element);
        },
        handlePostMessage(event) {
            if (event && event.data && event.origin == postMessagesSource) {
                let data = event.data;
                if (data && data.Element) {
                    dtm.trackScatterPlot(data.Element);
                }
            }
        },
        setTab(tabName) {
            if (!tabName) {
                return urlUtil.removeHash();
            }

            this.viewModel.attr('isAnyTabSelected', true);
            this.viewModel.setTab(
                this.tabs,
                this.tabsContent,
                this.element.find(`.s-tabs__item[data-name="${tabName}"]`),
                tabName,
            );
            this.viewModel.setTabContentState.call(this, tabName);
        },
        setDefaultTab() {
            const hash = urlUtil.getHash().slice(1);
            const defaultTab = this.viewModel.attr('defaultTab');
            const tabs = this.viewModel.attr('tabs');

            if (hash === '' || tabs.every(({ value }) => value !== hash)) {
                urlUtil.setHash(defaultTab);
                return this.setTab(defaultTab);
            }

            return this.setTab(hash);
        },
        '.s-tabs__item click'($el) {
            const name = $el.attr('data-name');
            urlUtil.setHash(name);
        },
        initTabs: function(element) {
            this.$tabs = $(element).find(TABS_SELECTOR);
            for (var i = 0; i < this.$tabs.length; ++i) {
                $(this.$tabs[i]).on(
                    'keydown',
                    function(e) {
                        this.switchTabOnArrowPress(e);
                    }.bind(this),
                );
                this.$tabs[i].index = i;
            }
        },
        switchTabOnArrowPress: function(event) {
            const pressed = event.keyCode;

            if (direction[pressed]) {
                const target = event.target;
                if (target.index !== undefined) {
                    if (this.$tabs[target.index + direction[pressed]]) {
                        this.$tabs[target.index + direction[pressed]].focus();
                    } else if (pressed === keys.left || pressed === keys.up) {
                        this.focusLastTab();
                    } else if (
                        pressed === keys.right ||
                        pressed === keys.down
                    ) {
                        this.focusFirstTab();
                    }
                }
            }
        },

        focusFirstTab: function() {
            this.$tabs[0].focus();
        },

        focusLastTab: function() {
            this.$tabs[this.$tabs.length - 1].focus();
        },
        removed() {
            window.removeEventListener(
                'message',
                this.handlePostMessage,
                false,
            );
        },
    },
});
